import { Injectable } from '@angular/core';
import { Contact } from '@app/contacts/models/contact';
import { DbWorkerService } from '@app/core/db/db-worker.service';
import { skySwitchDB } from '@app/core/db/skyswitch-db';
import { Document as FlexDocument, DocumentData } from 'flexsearch';
import { Subject } from 'rxjs';

import { FaxMessage } from '../models/fax.models';

/** Max items persisted to IDB. In-memory cache grows beyond this during the session. */
export const FAX_MESSAGES_CACHE_BOUND = 500;

interface IndexedFaxMessage extends DocumentData {
  id: string;
  callerIdNumber: string;
  destinationNumber: string;
  contactName: string;
}

@Injectable({
  providedIn: 'root',
})
export class FaxMessageStorageService {
  // ========== FlexSearch Index ==========

  private readonly searchIndex: FlexDocument<IndexedFaxMessage>;

  private readonly searchIndexReadySubject = new Subject<void>();
  public readonly searchIndexReady$ = this.searchIndexReadySubject.asObservable();

  private static readonly NAME_RELATED_FIELDS: Array<keyof Contact> = ['fullName', 'firstName', 'lastName', 'company'];

  // ========== In-Memory Store ==========

  private readonly itemsMap = new Map<string, FaxMessage>();
  private sortedItems: FaxMessage[] = []; // Sorted by created_at DESC

  private readonly cacheChangedSubject = new Subject<void>();

  /**
   * Observable that emits when the in-memory cache changes.
   * DataSources subscribe to this to know when to refresh their snapshot.
   */
  public readonly bulkDataChanged$ = this.cacheChangedSubject.asObservable();

  constructor(private readonly dbWorker: DbWorkerService) {
    this.searchIndex = new FlexDocument<IndexedFaxMessage>({
      document: {
        id: 'id',
        index: [
          { field: 'callerIdNumber', tokenize: 'full', encoder: 'Exact' },
          { field: 'destinationNumber', tokenize: 'full', encoder: 'Exact' },
          { field: 'contactName', tokenize: 'full', encoder: 'LatinDefault' },
        ],
      },
    });

    this.setUpContactObservers();
  }

  // ========== Seed ==========

  /**
   * Seed the in-memory cache from IDB data (called once at startup).
   * Items are expected to be sorted by created_at DESC from the worker.
   */
  public seedInMemory(items: FaxMessage[]): void {
    const startTime = performance.now();

    this.itemsMap.clear();
    this.sortedItems = [];

    for (const item of items) {
      this.itemsMap.set(item.id, item);
      this.sortedItems.push(item);
    }

    this.buildSearchIndex();
    this.cacheChangedSubject.next();

    const elapsed = performance.now() - startTime;
    console.log(`[Perf][fax-messages] seedInMemory: ${items.length} items seeded in ${elapsed.toFixed(1)}ms`);
  }

  // ========== Search Index Management ==========

  public buildSearchIndex(): void {
    const startTime = performance.now();
    this.searchIndex.clear();

    for (const item of this.sortedItems) {
      this.addToSearchIndex(item);
    }

    const elapsed = performance.now() - startTime;
    console.log(
      `[Perf][Fax-Search] Search index built: ${this.sortedItems.length} items indexed in ${elapsed.toFixed(1)}ms`
    );
    this.searchIndexReadySubject.next();
  }

  public addToSearchIndex(item: FaxMessage): void {
    const payload: IndexedFaxMessage = {
      id: item.id,
      callerIdNumber: item.caller_id ?? '',
      destinationNumber: String(item.destination ?? ''),
      contactName: item.contact?.fullName?.toLowerCase() ?? '',
    };

    this.searchIndex.add(payload);
  }

  public updateInSearchIndex(item: FaxMessage): void {
    const payload: IndexedFaxMessage = {
      id: item.id,
      callerIdNumber: item.caller_id ?? '',
      destinationNumber: String(item.destination ?? ''),
      contactName: item.contact?.fullName?.toLowerCase() ?? '',
    };

    this.searchIndex.update(payload);
  }

  public removeFromSearchIndex(id: string): void {
    this.searchIndex.remove(id);
  }

  public search(keyword: string): string[] {
    const query = keyword.toLowerCase();

    const results = this.searchIndex.search(query, {
      index: ['callerIdNumber', 'destinationNumber', 'contactName'],
      limit: 10000,
    });

    const ids = Array.from(new Set(results.flatMap((r) => r.result))) as string[];

    return ids;
  }

  // ========== Read Methods (synchronous) ==========

  public get(id: string): FaxMessage | undefined {
    return this.itemsMap.get(id);
  }

  public getAll(): FaxMessage[] {
    return [...this.sortedItems];
  }

  public getCount(predicate?: (item: FaxMessage) => boolean): number {
    if (predicate) {
      return this.sortedItems.filter(predicate).length;
    }
    return this.sortedItems.length;
  }

  public getFilteredItems(predicate: (item: FaxMessage) => boolean): FaxMessage[] {
    return this.sortedItems.filter(predicate);
  }

  public getItemsInRange(start: number, limit: number, predicate?: (item: FaxMessage) => boolean): FaxMessage[] {
    if (predicate) {
      return this.sortedItems.filter(predicate).slice(start, start + limit);
    }
    return this.sortedItems.slice(start, start + limit);
  }

  public getAllSortedItems(): FaxMessage[] {
    return [...this.sortedItems];
  }

  public getByIds(ids: string[]): FaxMessage[] {
    if (ids.length === 0) {
      return [];
    }
    return ids.map((id) => this.itemsMap.get(id)).filter((item): item is FaxMessage => !!item);
  }

  public getByIdsInRange(ids: string[], start: number, limit: number): FaxMessage[] {
    const paginatedIds = ids.slice(start, start + limit);
    return this.getByIds(paginatedIds);
  }

  // ========== Write Methods (memory-first + fire-and-forget IDB) ==========

  public upsertItems(items: FaxMessage[]): void {
    if (items.length === 0) {
      return;
    }

    for (const item of items) {
      const existing = this.itemsMap.get(item.id);
      if (existing) {
        if (existing.created_at !== item.created_at) {
          const idx = this.sortedItems.indexOf(existing);
          if (idx !== -1) {
            this.sortedItems.splice(idx, 1);
          }
          Object.assign(existing, item);
          this.insertSorted(existing);
        } else {
          Object.assign(existing, item);
        }
        this.updateInSearchIndex(existing);
      } else {
        this.insertSorted(item);
        this.addToSearchIndex(item);
      }
    }

    this.cacheChangedSubject.next();

    if (items.length <= 10) {
      const mergedItems = items.map((i) => this.itemsMap.get(i.id) ?? i);
      this.dbWorker.callWorker('bulkPut', { table: 'faxes', items: mergedItems });
    } else {
      this.writeBackToIDB();
    }
  }

  public updateItem(id: string, fieldsToUpdate: Partial<FaxMessage>): void {
    const existing = this.itemsMap.get(id);
    if (existing) {
      Object.assign(existing, fieldsToUpdate);
      this.updateInSearchIndex(existing);
      this.cacheChangedSubject.next();
      this.dbWorker.callWorker('bulkPut', { table: 'faxes', items: [existing] });
    } else {
      skySwitchDB.faxes.update(id, { ...fieldsToUpdate });
    }
  }

  public markAsRead(id: string, read: boolean): void {
    this.updateItem(id, { unread: !read, read });
  }

  public delete(id: string): void {
    this.removeFromMemory(id);
    this.cacheChangedSubject.next();
    this.dbWorker.callWorker('bulkDelete', { table: 'faxes', keys: [id] });
  }

  public bulkDelete(ids: string[]): void {
    for (const id of ids) {
      this.removeFromMemory(id);
    }
    this.cacheChangedSubject.next();
    this.dbWorker.callWorker('bulkDelete', { table: 'faxes', keys: ids });
  }

  public trimToOriginalBound(): void {
    const bound = FAX_MESSAGES_CACHE_BOUND;
    if (this.sortedItems.length <= bound) {
      return;
    }

    const excess = this.sortedItems.splice(bound);
    for (const item of excess) {
      this.itemsMap.delete(item.id);
      this.removeFromSearchIndex(item.id);
    }

    this.cacheChangedSubject.next();
    this.writeBackToIDB();
  }

  public async drop(): Promise<void> {
    this.itemsMap.clear();
    this.sortedItems = [];
    this.searchIndex.clear();
    this.cacheChangedSubject.next();
    await skySwitchDB.resetFaxMessages();
  }

  public getUnreadCounts(): { total: number; byNumber: Record<string, number> } {
    let total = 0;
    const byNumber: Record<string, number> = {};
    for (const fax of this.sortedItems) {
      if (fax.unread) {
        total++;
        const num = String(fax.destination);
        byNumber[num] = (byNumber[num] || 0) + 1;
      }
    }
    return { total, byNumber };
  }

  // ========== Private Helpers ==========

  /**
   * Insert an item in sorted position (created_at DESC) using binary search.
   * Also sets itemsMap.
   */
  private insertSorted(item: FaxMessage): void {
    this.itemsMap.set(item.id, item);

    let low = 0;
    let high = this.sortedItems.length;
    const itemTime = item.created_at;

    while (low < high) {
      const mid = (low + high) >>> 1;
      // DESC: items with later timestamps come first
      if (this.sortedItems[mid].created_at >= itemTime) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    this.sortedItems.splice(low, 0, item);
  }

  private removeFromMemory(id: string): void {
    const existing = this.itemsMap.get(id);
    if (!existing) {
      return;
    }
    this.itemsMap.delete(id);
    const idx = this.sortedItems.indexOf(existing);
    if (idx !== -1) {
      this.sortedItems.splice(idx, 1);
    }
    this.removeFromSearchIndex(id);
  }

  /**
   * Write the most recent FAX_MESSAGES_CACHE_BOUND items to IDB and evict excess.
   * In-memory cache retains all items for the session.
   */
  private writeBackToIDB(): void {
    const bound = FAX_MESSAGES_CACHE_BOUND;
    const itemsToWrite = this.sortedItems.slice(0, bound);
    this.dbWorker.callWorker('bulkPut', { table: 'faxes', items: itemsToWrite });

    if (this.sortedItems.length > bound) {
      const excessIds = this.sortedItems.slice(bound).map((item) => item.id);
      this.dbWorker.callWorker('bulkDelete', { table: 'faxes', keys: excessIds });
    }
  }

  // ========== Contact Change Handling ==========

  private setUpContactObservers(): void {
    skySwitchDB.contactUpdatedSubject.asObservable().subscribe(({ contact, mods }) => {
      const nameChanged = FaxMessageStorageService.NAME_RELATED_FIELDS.some((field) => field in mods);

      if (nameChanged) {
        this.reindexFaxMessagesForContact(contact);
      }
    });
  }

  private reindexFaxMessagesForContact(contact: Contact): void {
    for (const item of this.sortedItems) {
      if (item.contact?.id === contact.id) {
        item.contact = contact;
        this.updateInSearchIndex(item);
      }
    }
  }
}
